import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { DAILY_SUMMARY } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const PAGE_SIZE = 50;
const DEFAULT_END_DATE = format(new Date(), 'yyyy-MM-dd');
const DEFAULT_START_DATE = format(subDays(new Date(), 30), 'yyyy-MM-dd');

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString('en-US') : '—';
};

const formatRatio = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : '—';
};

const DailySummaryReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const { startDate, endDate, setDateRange } = useDateRangeParams(DEFAULT_START_DATE, DEFAULT_END_DATE);

  const columns = useMemo(
    () => [
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'total_revenue', label: 'Total Revenue', type: 'currency' },
      { key: 'total_orders', label: 'Total Orders', type: 'number' },
      { key: 'total_spend', label: 'Total Ad Spend', type: 'currency' },
      { key: 'mer', label: 'MER', type: 'number' },
      { key: 'shopify_revenue', label: 'Shopify Revenue', type: 'currency' },
      { key: 'shopify_orders', label: 'Shopify Orders', type: 'number' },
      { key: 'shopify_taxes', label: 'Shopify Taxes', type: 'currency' },
      { key: 'shopify_refunds', label: 'Shopify Refunds', type: 'currency' },
      { key: 'shopify_spend', label: 'Shopify Ad Spend', type: 'currency' },
      { key: 'amazon_revenue', label: 'Amazon Revenue', type: 'currency' },
      { key: 'amazon_orders', label: 'Amazon Orders', type: 'number' },
      { key: 'amazon_spend', label: 'Amazon PPC Spend', type: 'currency' },
      { key: 'amazon_ppc_sales', label: 'Amazon PPC Sales', type: 'currency' },
      { key: 'amazon_roas', label: 'Amazon ROAS', type: 'number' },
    ],
    []
  );

  const buildParams = useCallback(
    (extra = {}) =>
      new URLSearchParams({
        startDate,
        endDate,
        ...extra,
      }),
    [startDate, endDate]
  );

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = buildParams({ page: String(currentPage), pageSize: String(PAGE_SIZE) });
      const res = await fetch(`/api/v1/reports/${DAILY_SUMMARY}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch report data');
      }
      const json = await res.json();
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setTotalPages(json.totalPages || 1);
      setTotalRecords(json.totalRecords || 0);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      if (!isLatest(requestId)) return;
      setError(e.message);
      setData([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [startDate, endDate, currentPage, buildParams, beginRequest, isLatest]);

  useEffect(() => {
    if (!initialReady) return;
    fetchData();
  }, [initialReady, fetchData]);

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const renderRows = () =>
    (data || []).map((row, idx) => (
      <tr key={`${row.date || 'date'}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.date || ''}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium text-right">{formatCurrency(row.total_revenue)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatNumber(row.total_orders)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatCurrency(row.total_spend)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{formatRatio(row.mer)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.shopify_revenue)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatNumber(row.shopify_orders)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.shopify_taxes)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.shopify_refunds)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.shopify_spend)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.amazon_revenue)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatNumber(row.amazon_orders)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.amazon_spend)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatCurrency(row.amazon_ppc_sales)}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{formatRatio(row.amazon_roas)}</td>
      </tr>
    ));

  const filtersComponent = (
    <div className="contents">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        onDateChange={({ startDate: newStartDate, endDate: newEndDate }) => {
          setDateRange({ startDate: newStartDate, endDate: newEndDate });
          setCurrentPage(1);
        }}
        label="Date Range"
      />
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={data}
      columns={columns}
      filename={DAILY_SUMMARY}
      onExport={async () => {
        const params = buildParams();
        const res = await fetch(`/api/v1/reports/${DAILY_SUMMARY}/export?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Export failed');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const contentDisposition = res.headers.get('content-disposition');
        const exportFilename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `${DAILY_SUMMARY}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.href = url;
        link.download = exportFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, handled: true };
      }}
      disabled={isLoading}
      variant="outline"
    />
  );

  const paginationComponent =
    totalPages > 1 ? (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing {(currentPage - 1) * PAGE_SIZE + 1} to {Math.min(currentPage * PAGE_SIZE, totalRecords)} of{' '}
          {totalRecords} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  return (
    <ReportTemplate
      reportId={DAILY_SUMMARY}
      description="One row per day (America/Denver): blended total revenue, orders, ad spend and MER, with Shopify and Amazon columns side by side — KPI-tracker style"
      breadcrumbs={breadcrumbs}
      onBack={handleBackToReports}
      marginFromSidebar={marginFromSidebar}
      isLoading={isLoading}
      error={error}
      filters={filtersComponent}
      exportButton={exportButton}
      data={data}
      columns={columns}
      pagination={paginationComponent}
      emptyMessage="No daily summary rows found for the selected date range. Data is populated by the Daily Channel Metrics cron."
    >
      {renderRows()}
    </ReportTemplate>
  );
};

export default DailySummaryReport;
